<script setup lang="ts">
import { useVuelidate } from "@vuelidate/core";
import { customValidators } from "@/i18n/utils/i18n-validators";
import { useCmsTranslations } from "@shopware-pwa/composables-next";
import { ApiClientError } from "@shopware/api-client";
import type { ApiError } from "@shopware/api-client";
import {
  useNavigationContext,
  useSalutations,
  useShopwareContext,
} from "#imports";
import { computed, reactive, ref } from "vue";
import { defu } from "defu";
import { useI18n } from "vue-i18n";
import { helpers } from "@vuelidate/validators";

type Translations = {
  form: {
    salutation: string;
    salutationPlaceholder: string;
    first_name: string;
    first_namePlaceholder: string;
    last_name: string;
    last_namePlaceholder: string;
    email: string;
    emailPlaceholder: string;
    phone: string;
    phonePlaceholder: string;
    subject: string;
    subjectPlaceholder: string;
    message: string;
    messagePlaceholder: string;
    privacy: string;
    dataProtection: string;
    submit: string;
    messages: {
      contactFormSuccess: string;
    };
  };
};

let translations: Translations = {
  form: {
    salutation: "Salutation",
    salutationPlaceholder: "Enter salutation...",
    first_name: "First name",
    first_namePlaceholder: "Enter first name...",
    last_name: "Last name",
    last_namePlaceholder: "Enter last name...",
    email: "Email address",
    emailPlaceholder: "Enter email address...",
    phone: "Phone number",
    phonePlaceholder: "Enter phone number...",
    subject: "Subject",
    subjectPlaceholder: "Enter subject...",
    message: "message",
    messagePlaceholder: "Enter message...",
    privacy: "Privacy",
    dataProtection: "I have read the data protection information.",
    submit: "Submit",
    messages: {
      contactFormSuccess:
        "We have received your contact request and will process it as soon as possible.",
    },
  },
};

translations = defu(useCmsTranslations(), translations) as Translations;

const { required, minLength, email } = customValidators();
const { executeRecaptcha } = useGoogleRecaptcha();

const loading = ref<boolean>();
const formSent = ref<boolean>(false);
const { t } = useI18n();
type ErrorMessages = ApiClientError<{
  errors: ApiError[];
}>;

const errorMessages = ref<ErrorMessages[]>([]);

const { getSalutations } = useSalutations();
const { foreignKey } = useNavigationContext();
const { apiClient } = useShopwareContext();

const state = reactive({
  recipient_first_name: "Team",
  recipient_last_name: "Kisag",
  recipient_email: "shop@kisag.ch",
  salutation: "",
  first_name: "",
  last_name: "",
  email: "",
  subject: t("form.subject.contact"),
  user_subject_line: "",
  message: "",
  phone: "",
  checkbox: false,
});

const rules = computed(() => ({
  email: {
    required,
    email,
  },
  first_name: {
    required,
    minLength: minLength(3),
  },
  last_name: {
    required,
    minLength: minLength(3),
  },
  salutation: {
    required,
  },
  phone: {
    required,
    minLength: minLength(3),
  },
  user_subject_line: {
    required,
    minLength: minLength(3),
  },
  message: {
    required,
    minLength: minLength(10),
  },
  checkbox: {
    required,
    isTrue: (value: boolean) => value === true,
  },
}));

const isSubmited = ref<boolean>(false);
const $v = useVuelidate(rules, state);
const formElement = ref<HTMLFormElement | null>(null);

const invokeSubmit = async () => {
  $v.value.$touch();
  const valid = await $v.value.$validate();
  if (valid) {
    const { token } = await executeRecaptcha('contactform');
    isSubmited.value = true;
    loading.value = true;
    // remove checkbox from payload
    const { checkbox, ...payload } = state;
    try {
      await apiClient.invoke(
        "sendCustomContactMail post /g2f_ht/contact-form",
        {
          body: {
            ...payload,
            '_grecaptcha_v3': token,
            // navigationId: foreignKey.value,
          },
        }
      );
      formSent.value = true;
    } catch (e) {
      if (e instanceof ApiClientError) {
        errorMessages.value = e.details.errors;
      }
      loading.value = false;
    } finally {
      nextTick(() => {
        formElement.value?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
      loading.value = false;
      isSubmited.value = false;
    }
  }
};
</script>
<template>
  <form
    class="max-w-4xl mx-auto custom-form scroll-mt-64"
    ref="formElement"
    @submit.prevent="invokeSubmit"
  >
    <template v-if="!formSent">
      <div
        class="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-3 mb-4 lg:mb-12 gap-y-4 gap-x-2 md:gap-x-7"
      >
        <div class="relative">
          <select
            id="salutation"
            v-model="state.salutation"
            name="salutation"
            class="input-field input-sty peer placeholder-transparent"
            :class="[
              $v.salutation.$error
                ? 'border-red-600 focus:border-red-600 border-2'
                : 'border-gray-300 focus:border-gray-500',
            ]"
            @blur="$v.salutation.$touch()"
          >
            <option
              v-for="salutation in getSalutations"
              :key="salutation.id"
              :value="salutation.translated.displayName"
            >
              {{ salutation.translated.displayName }}
            </option>
          </select>
          <label
            for="salutation"
            class="input-label top-3 label-sty peer-focus:transform peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-dark-bg peer-focus:px-2"
            :class="{
              'transform -translate-y-5 text-xs px-2':
                state.salutation || $v.salutation.$error,
              'text-red-500': $v.salutation.$error,
              'text-dark-bg': !$v.salutation.$error,
            }"
          >
            {{ $t("form.salutation") }} *
          </label>
          <span v-if="$v.salutation.$error" class="form-error text-xs">
            {{ $v.salutation.$errors[0].$message }}
          </span>
        </div>
        <div class="relative">
          <input
            id="first-name"
            v-model="state.first_name"
            name="first-name"
            type="text"
            autocomplete="first-name"
            class="input-field input-sty peer placeholder-transparent"
            :class="[
              $v.first_name.$error
                ? 'border-red-600 focus:border-red-600 border-2 border-2'
                : 'border-gray-300 focus:border-gray-500',
            ]"
            @blur="$v.first_name.$touch()"
          />
          <label
            for="first-name"
            class="input-label top-3 label-sty peer-focus:transform peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-dark-bg peer-focus:px-2"
            :class="{
              'transform -translate-y-5 text-xs px-2':
                state.first_name || $v.first_name.$error,
              'text-red-500': $v.first_name.$error,
              'text-dark-bg': !$v.first_name.$error,
            }"
          >
            {{ $t("form.firstName") }} *
          </label>
          <span v-if="$v.first_name.$error" class="form-error text-xs">
            {{ $v.first_name.$errors[0].$message }}
          </span>
        </div>
        <div class="relative">
          <input
            id="last-name"
            v-model="state.last_name"
            name="last-name"
            type="text"
            autocomplete="last-name"
            class="input-field input-sty peer placeholder-transparent focus:border-2 focus:border-dark-bg"
            :class="[
              $v.last_name.$error
                ? 'border-red-600 focus:border-red-600 border-2'
                : 'border-gray-300 focus:border-gray-500',
            ]"
            @blur="$v.last_name.$touch()"
          />
          <label
            for="last-name"
            class="input-label top-3 label-sty peer-focus:transform peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-dark-bg peer-focus:px-2"
            :class="{
              'transform -translate-y-5 text-xs px-2':
                state.last_name || $v.last_name.$error,
              'text-red-500': $v.last_name.$error,
              'text-dark-bg': !$v.last_name.$error,
            }"
          >
            {{ $t("form.lastName") }} *
          </label>
          <span v-if="$v.last_name.$error" class="form-error text-xs">
            {{ $v.last_name.$errors[0].$message }}
          </span>
        </div>
      </div>
      <div
        class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 mb-4 lg:mb-12 gap-y-4 gap-x-2 md:gap-x-7"
      >
        <div class="relative">
          <input
            id="email-address"
            v-model="state.email"
            name="email"
            type="email"
            autocomplete="email"
            class="input-field input-sty peer placeholder-transparent"
            :class="[
              $v.email.$error
                ? 'border-red-600 focus:border-red-600 border-2'
                : 'border-gray-300 focus:border-gray-500',
            ]"
            @blur="$v.email.$touch()"
          />
          <label
            for="email-address"
            class="input-label top-3 label-sty peer-focus:transform peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-dark-bg peer-focus:px-2"
            :class="{
              'transform -translate-y-5 text-xs px-2':
                state.email || $v.email.$error,
              'text-red-500': $v.email.$error,
              'text-dark-bg': !$v.email.$error,
            }"
          >
            {{ $t("form.email") }} *
          </label>

          <span v-if="$v.email.$error" class="form-error text-xs">
            {{ $v.email.$errors[0].$message }}
          </span>
        </div>
        <div class="relative">
          <input
            id="phone"
            v-model="state.phone"
            name="phone"
            type="text"
            autocomplete="phone"
            class="input-field input-sty peer placeholder-transparent"
            :class="[
              $v.phone.$error
                ? 'border-red-600 focus:border-red-600 border-2'
                : 'border-gray-300 focus:border-gray-500',
            ]"
            @blur="$v.phone.$touch()"
          />
          <label
            for="phone"
            class="input-label top-3 label-sty peer-focus:transform peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-dark-bg peer-focus:px-2"
            :class="{
              'transform -translate-y-5 text-xs px-2':
                state.phone || $v.phone.$error,
              'text-red-500': $v.phone.$error,
              'text-dark-bg': !$v.phone.$error,
            }"
          >
            {{ $t("form.phone") }} *
          </label>

          <span v-if="$v.phone.$error" class="form-error text-xs">
            {{ $v.phone.$errors[0].$message }}
          </span>
        </div>
      </div>

      <!-- ------hidden required fields-->
      <div class="grid w-full mb-4 lg:mb-12">
        <div class="relative">
          <input
            id="subject"
            v-model="state.user_subject_line"
            name="subject"
            type="text"
            autocomplete="subject"
            class="input-field input-sty peer placeholder-transparent"
            :class="[
              $v.user_subject_line.$error
                ? 'border-red-600 focus:border-red-600 border-2'
                : 'border-gray-300 focus:border-gray-500',
            ]"
            @blur="$v.user_subject_line.$touch()"
          />
          <label
            for="subject"
            class="input-label top-3 label-sty peer-focus:transform peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-dark-bg peer-focus:px-2"
            :class="{
              'transform -translate-y-5 text-xs px-2':
                state.user_subject_line || $v.user_subject_line.$error,
              'text-red-500': $v.user_subject_line.$error,
              'text-dark-bg': !$v.user_subject_line.$error,
            }"
          >
            {{ $t("form.subject") }} *
          </label>

          <span v-if="$v.user_subject_line.$error" class="form-error text-xs">
            {{ $v.user_subject_line.$errors[0].$message }}
          </span>
        </div>
      </div>
      <div class="grid w-full mb-4 lg:mb-12">
        <div class="relative">
          <textarea
            id="message"
            v-model="state.message"
            name="message"
            type="text"
            autocomplete="message"
            class="input-field input-sty peer placeholder-transparent"
            :class="[
              $v.message.$error
                ? 'border-red-600 focus:border-red-600 border-2'
                : 'border-gray-300 focus:border-gray-500',
            ]"
            rows="5"
            @blur="$v.message.$touch()"
          />
          <label
            for="message"
            class="input-label top-3 label-sty peer-focus:transform peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-dark-bg peer-focus:px-2"
            :class="{
              'transform -translate-y-5 text-xs px-2':
                state.message || $v.message.$error,
              'text-red-500': $v.message.$error,
              'text-dark-bg': !$v.message.$error,
            }"
          >
            {{ $t("form.comment") }} *
          </label>
          <span v-if="$v.message.$error" class="form-error text-xs">
            {{ $v.message.$errors[0].$message }}
          </span>
        </div>
      </div>
      <div
        class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 mb-4 lg:mb-12 gap-y-4 gap-x-2 md:gap-x-7"
      >
        <div class="col-span-12 flex items-center justify-center">
          <label
            class="relative flex cursor-pointer items-center rounded-full p-3"
            for="ripple-on"
          >
            <input
              id="ripple-on"
              type="checkbox"
              v-model="state.checkbox"
              @blur="$v.checkbox.$touch()"
              class="peer relative h-5 w-5 cursor-pointer appearance-none rounded border border-custom-grey shadow hover:shadow-md transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-dark-bg before:opacity-0 before:transition-opacity checked:border-dark-bg checked:bg-dark-bg checked:before:bg-slate-400 hover:before:opacity-10"
            />
            <span
              class="pointer-events-none absolute top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 text-white opacity-0 transition-opacity peer-checked:opacity-100"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-3.5 w-3.5"
                viewBox="0 0 20 20"
                fill="currentColor"
                stroke="currentColor"
                stroke-width="1"
              >
                <path
                  fill-rule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clip-rule="evenodd"
                ></path>
              </svg>
            </span>
          </label>
          <label
            class="cursor-pointer text-sm"
            for="ripple-on"
            :class="[
              $v.checkbox.$error
                ? 'text-red-600 focus:text-red-600'
                : 'text-gray-600 focus:text-gray-500',
            ]"
            >{{ $t("checkout.termsAdnConditionsLabel") }}</label
          >
        </div>
      </div>
      <!-- ------hidden required fields end -->

      <div class="text-center flex justify-center">
        <button
          type="submit"
          class="btn-default btn-black-bg btn-w-icon"
          :disabled="isSubmited"
          :class="{ 'opacity-50 cursor-not-allowed': isSubmited }"
        >
          {{ $t("form.submit") }}
          <span class="ml-3" v-if="!isSubmited">
            <i class="fa-solid fa-angle-right"></i>
          </span>
          <span class="ml-3" v-else>
            <i class="fa-solid fa-spinner animate-spin"></i>
          </span>
        </button>
      </div>
    </template>
    <template v-else>
      <div class="py-10 text-lg text-center font-bold">
        <i class="fa-solid fa-thumbs-up"></i>
        <p>{{ $t("form.success") }}</p>
      </div>
    </template>
  </form>
</template>
